import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logout, reloadUser, resendVerification } from "../lib/firebase";
import { useAuth } from "../state/AuthContext";
import { Button } from "../components/common/Button";
import "./auth.css";

export function VerifyEmail() {
  const navigate = useNavigate();
  const { user, isVerified, refresh } = useAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);

  // If already verified, move on.
  useEffect(() => {
    if (isVerified) navigate("/", { replace: true });
  }, [isVerified, navigate]);

  // Poll for verification periodically so the user doesn't have to click.
  useEffect(() => {
    const interval = setInterval(async () => {
      const u = await reloadUser();
      if (u?.emailVerified) {
        refresh();
        navigate("/", { replace: true });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [navigate, refresh]);

  const handleCheck = async () => {
    setError(null);
    setNotice(null);
    setChecking(true);
    try {
      const u = await reloadUser();
      if (u?.emailVerified) {
        refresh();
        navigate("/", { replace: true });
      } else {
        setNotice("Not verified yet — click the link in your email, then check again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't check status.");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setNotice(null);
    setResending(true);
    try {
      await resendVerification();
      setNotice("Verification email re-sent. Check your inbox (and spam).");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't resend right now — try again shortly.",
      );
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <span className="auth__logo" aria-hidden="true" />
          <div>
            <div className="auth__brand-text">LADP Scoping Assistant</div>
            <div className="auth__brand-sub">AI Singapore</div>
          </div>
        </div>

        <h1 className="auth__title">Verify your email</h1>
        <p className="auth__lede">
          We sent a verification link to{" "}
          <strong>{user?.email ?? "your email"}</strong>. Click it to unlock the
          workspace — this page will continue automatically once you do.
        </p>

        <div className="auth__form">
          {error && <div className="auth__error">{error}</div>}
          {notice && <div className="auth__notice">{notice}</div>}

          <Button variant="primary" block loading={checking} onClick={handleCheck}>
            I've verified — continue
          </Button>
          <Button
            variant="secondary"
            block
            loading={resending}
            onClick={handleResend}
          >
            Resend verification email
          </Button>
        </div>

        <div className="auth__footer">
          Wrong account?{" "}
          <button type="button" className="auth__link" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
